'use client'

import type { AnimationTiming } from 'recharts/types/util/types'
import type { Cashflow } from '@/types/statistics'

import {
  Toggle,
  ToggleGroup,
  ToggleSize,
} from '@moment/design-system-ui/toggle'
import { format } from 'date-fns/format'
import { parseISO } from 'date-fns/parseISO'
import { useMemo, useState } from 'react'
import { Rectangle } from 'recharts'

import {
  type BarSize,
  MultipleBarChart,
} from '@/components/atomic/bar-chart/multiple'
import { BarChartSkeleton } from '@/components/atomic/bar-chart/skeleton'
import { ChartLegend } from '@/components/atomic/chart-legend'
import { ChartTooltip } from '@/components/atomic/chart-tooltip'
import ChartTransitionWrapper from '@/components/atomic/chart-transition-wrapper'
import { BodySubtext } from '@/components/atomic/text'
import { AccountBarChartTimePeriod } from '@/types/enums/accounts'
import { DateFormat } from '@/utils/dates/dateFormatting'
import { formatMoney } from '@/utils/presentation/formatting'
import { cn } from '@/utils/shadcn/utils'

import {
  aggregateCashflows,
  hasCallCashflows,
  timeRangeOptions,
} from './helpers'
import {
  ChartContainer,
  HeaderContainer,
  HeaderContent,
  SwitchContainer,
} from './styles'

interface Cashflows {
  monthly_cashflows: Cashflow[]
  yearly_cashflows: Cashflow[]
}

const DEFAULT_CHART_HEIGHT = 246
// Skeleton bars sit inside the chart's top/bottom margins and axis band.
const SKELETON_BAR_INSET = 46

interface InvestmentIncomeBarChartProps {
  cashflows: Cashflows
  isLoading?: boolean
  title?: string
  onViewChange?: (view: string) => void
  contentClassName?: string
  currency?: string
  barSize?: BarSize
  /** Plot height in px; also sizes the loading skeleton. */
  chartHeight?: number
  /** Plot inset so edge bars don't touch the container. */
  xAxisPadding?: { left?: number; right?: number }
}

interface ChartData {
  month: string
  principal: number
  call: number
  coupon: number
  dividend: number
}

const TooltipFormatter = (
  dataPoint: ChartData,
  currency: string,
  hasCall: boolean,
) => {
  return (
    <ChartTooltip
      className="w-48"
      date={dataPoint.month}
      values={[
        {
          label: 'Principal',
          value: formatMoney(dataPoint.principal, { currency }),
          color: 'var(--color-bar-moment-chart-forest-full)',
        },
        ...(hasCall
          ? [
              {
                label: 'Call',
                value: formatMoney(dataPoint.call, { currency }),
                color: 'var(--color-bar-moment-chart-blue-grey-full)',
              },
            ]
          : []),
        {
          label: 'Coupon',
          value: formatMoney(dataPoint.coupon, { currency }),
          color: 'var(--color-bar-moment-chart-forest-half)',
        },
        {
          label: 'Dividend',
          value: formatMoney(dataPoint.dividend, { currency }),
          color: 'var(--color-moment-teal-600)',
        },
      ]}
    />
  )
}

const TOP_RADIUS: [number, number, number, number] = [4, 4, 0, 0]
const NO_RADIUS: [number, number, number, number] = [0, 0, 0, 0]

const CallShape = (props: unknown) => {
  const barProps = props as Record<string, unknown>
  const payload = barProps.payload as
    | { coupon?: number; dividend?: number }
    | undefined
  const radius =
    payload?.coupon === 0 && payload?.dividend === 0 ? TOP_RADIUS : NO_RADIUS
  return <Rectangle {...barProps} radius={radius} />
}

const CouponShape = (props: unknown) => {
  const barProps = props as Record<string, unknown>
  const payload = barProps.payload as { dividend?: number } | undefined
  const radius = payload?.dividend === 0 ? TOP_RADIUS : NO_RADIUS
  return <Rectangle {...barProps} radius={radius} />
}

const animation: Record<
  string,
  {
    animationBegin: number
    animationDuration: number
    animationEasing: AnimationTiming
  }
> = {
  // First animate the principal stack
  principal: {
    animationBegin: 0,
    animationDuration: 80,
    animationEasing: 'ease-in',
  },
  // Animate calls right after principal; both are redemptions.
  call: {
    animationBegin: 80,
    animationDuration: 20,
    animationEasing: 'ease-out',
  },
  // Once the redemption stacks are done, animate the coupons.
  // Use a faster animation because coupons are generally
  // far smaller than principal.
  coupon: {
    animationBegin: 100,
    animationDuration: 20,
    animationEasing: 'ease-out',
  },
  // Animate dividends after coupons.
  dividend: {
    animationBegin: 120,
    animationDuration: 20,
    animationEasing: 'ease-out',
  },
}

export function InvestmentIncomeBarChart({
  cashflows,
  isLoading,
  title,
  onViewChange,
  contentClassName,
  currency = 'USD',
  barSize,
  chartHeight = DEFAULT_CHART_HEIGHT,
  xAxisPadding,
}: InvestmentIncomeBarChartProps) {
  const [timeRange, setTimeRange] = useState<string>(
    AccountBarChartTimePeriod.Annual,
  )

  const isMonthly = useMemo(
    () => timeRange === AccountBarChartTimePeriod.Monthly,
    [timeRange],
  )

  const currentCashflows = useMemo(
    () =>
      isMonthly ? cashflows.monthly_cashflows : cashflows.yearly_cashflows,
    [cashflows, isMonthly],
  )

  // The call series only renders when the data has call rows, so
  // flag-off accounts never see an empty "Call payment" legend.
  const showCallSeries = useMemo(() => hasCallCashflows(cashflows), [cashflows])

  const aggregatedData = aggregateCashflows(currentCashflows)

  const chartData = aggregatedData.map((cashflow) => ({
    month: format(
      parseISO(cashflow.date),
      isMonthly ? DateFormat.YEAR_MONTH : DateFormat.YEAR,
    ), // Formats the date based on the time range -- Monthly: October 2023, Annual: 2023
    principal: cashflow.principal,
    call: cashflow.call,
    coupon: cashflow.coupon,
    dividend: cashflow.dividend,
  }))

  return (
    <ChartContainer className="relative">
      <HeaderContainer>
        {title && (
          <BodySubtext color="text-secondary" className="text-left">
            {title}
          </BodySubtext>
        )}
        <HeaderContent
          className={cn(
            'transition-opacity duration-300',
            isLoading && 'opacity-0 pointer-events-none',
          )}
        >
          <ChartLegend
            items={[
              {
                color: 'var(--color-bar-moment-chart-forest-full)',
                label: 'Principal payment',
              },
              ...(showCallSeries
                ? [
                    {
                      color: 'var(--color-bar-moment-chart-blue-grey-full)',
                      label: 'Call payment',
                    },
                  ]
                : []),
              {
                color: 'var(--color-bar-moment-chart-forest-half)',
                label: 'Coupon payment',
              },
              {
                color: 'var(--color-moment-teal-600)',
                label: 'Dividend payment',
              },
            ]}
          />
          <SwitchContainer>
            <ToggleGroup
              value={timeRange}
              onValueChange={(value) => {
                setTimeRange(value)
                onViewChange?.(value)
              }}
              size={ToggleSize.Small}
            >
              {timeRangeOptions.map((option) => (
                <Toggle key={option.value} value={option.value}>
                  {option.label}
                </Toggle>
              ))}
            </ToggleGroup>
          </SwitchContainer>
        </HeaderContent>
      </HeaderContainer>
      {/* Inline style: the height is caller-configurable, so it can't be a
          static Tailwind class. */}
      <div style={{ height: chartHeight }}>
        <ChartTransitionWrapper
          isLoading={isLoading}
          height="h-full"
          loadingContent={
            <BarChartSkeleton
              height={chartHeight}
              barCount={10}
              barHeight={chartHeight - SKELETON_BAR_INSET}
              barSpacing={8}
              waveType="trending"
            />
          }
        >
          <MultipleBarChart
            data={chartData}
            xAxisKey="month"
            bars={[
              {
                key: `principal:${timeRange}`,
                dataKey: 'principal',
                fill: 'var(--color-bar-moment-chart-forest-full)',
                stackId: 'cashflow',
                radius: [0, 0, 0, 0],
                ...animation.principal,
              },
              ...(showCallSeries
                ? [
                    {
                      key: `call:${timeRange}`,
                      dataKey: 'call',
                      fill: 'var(--color-bar-moment-chart-blue-grey-full)',
                      stackId: 'cashflow',
                      shape: CallShape,
                      ...animation.call,
                    },
                  ]
                : []),
              {
                key: `coupon:${timeRange}`,
                dataKey: 'coupon',
                fill: 'var(--color-bar-moment-chart-forest-half)',
                stackId: 'cashflow',
                shape: CouponShape,
                ...animation.coupon,
              },
              {
                key: `dividend:${timeRange}`,
                dataKey: 'dividend',
                fill: 'var(--color-moment-teal-600)',
                stackId: 'cashflow',
                radius: [4, 4, 0, 0],
                ...animation.dividend,
              },
            ]}
            tooltipFormatter={(dataPoint: ChartData) =>
              TooltipFormatter(dataPoint, currency, showCallSeries)
            }
            barChartClassName={contentClassName}
            barSize={barSize}
            xAxisPadding={xAxisPadding}
          />
        </ChartTransitionWrapper>
      </div>
    </ChartContainer>
  )
}
